import { CheckCircle, FileAudio, Image, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import type { WorkSubcategoryInfo, WorkWizardState } from '../../types/work'

interface WorkWizardStep3Props {
  state: WorkWizardState
  subcategoryInfo?: WorkSubcategoryInfo
  onChange: (patch: Partial<WorkWizardState>) => void
  onNext: () => void
  onBack: () => void
}

const MAX_MB = 25

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FilePreview({ file }: { file: File }) {
  const isImage = file.type.startsWith('image/')
  const isAudio = file.type.startsWith('audio/')
  const [imgSrc, setImgSrc] = useState<string | null>(null)

  if (isImage && !imgSrc) {
    const reader = new FileReader()
    reader.onload = e => setImgSrc(e.target?.result as string)
    reader.readAsDataURL(file)
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: '8px',
        background: 'var(--card-bg)',
        border: '1px solid var(--border-color)',
      }}
    >
      {isImage && imgSrc ? (
        <img src={imgSrc} alt="preview" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }} />
      ) : isAudio ? (
        <FileAudio size={24} style={{ color: 'var(--primary, #6366f1)' }} />
      ) : (
        <Image size={24} style={{ color: 'var(--primary, #6366f1)' }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>{formatBytes(file.size)}</div>
      </div>
      <CheckCircle size={16} style={{ color: 'var(--success, #10b981)', flexShrink: 0 }} />
    </div>
  )
}

export default function WorkWizardStep3({ state, subcategoryInfo, onChange, onNext, onBack }: WorkWizardStep3Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [sizeError, setSizeError] = useState<string | null>(null)

  const isTextOnly = subcategoryInfo?.input_type === 'text'
  const acceptedTypes = subcategoryInfo?.accepted_file_types ?? []
  const acceptAttr = acceptedTypes.map(t => `.${t}`).join(',')

  function handleFile(file: File) {
    setSizeError(null)
    if (file.size > MAX_MB * 1024 * 1024) {
      setSizeError(`File too large. Max ${MAX_MB}MB allowed.`)
      return
    }
    onChange({ file })
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) {handleFile(file)}
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {handleFile(file)}
  }

  if (isTextOnly) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div
          style={{
            padding: '20px',
            borderRadius: '12px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: '13px',
          }}
        >
          <CheckCircle size={24} style={{ color: 'var(--success, #10b981)', marginBottom: '8px' }} />
          <div>No file needed for this task type.</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)', marginTop: '4px' }}>
            Your text input from the previous step will be used.
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onBack} style={{ padding: '10px 20px', borderRadius: '10px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer' }}>
            ← Back
          </button>
          <button onClick={onNext} style={{ padding: '10px 24px', borderRadius: '10px', background: 'var(--primary, #6366f1)', color: '#fff', border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
            Next →
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          padding: '40px 20px',
          borderRadius: '12px',
          border: `2px dashed ${dragOver ? 'var(--primary, #6366f1)' : 'var(--border-color)'}`,
          background: dragOver ? 'rgba(99, 102, 241, 0.05)' : 'var(--card-bg)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <Upload size={28} style={{ color: 'var(--text-muted, #6b7280)', marginBottom: '10px' }} />
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
          Drop file here or click to browse
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>
          Max {MAX_MB}MB
          {acceptedTypes.length > 0 && ` · ${acceptedTypes.join(', ')}`}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr || undefined}
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />
      </div>

      {/* Size error */}
      {sizeError && (
        <div style={{ fontSize: '12px', color: 'var(--danger, #ef4444)', padding: '8px 12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          {sizeError}
        </div>
      )}

      {/* File preview */}
      {state.file && (
        <div style={{ position: 'relative' }}>
          <FilePreview file={state.file} />
          <button
            onClick={() => onChange({ file: undefined })}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-muted, #6b7280)',
            }}
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ padding: '10px 20px', borderRadius: '10px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer' }}>
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={subcategoryInfo?.input_type === 'file' && !state.file}
          style={{
            padding: '10px 24px',
            borderRadius: '10px',
            background: (subcategoryInfo?.input_type === 'file' && !state.file) ? 'var(--border-color)' : 'var(--primary, #6366f1)',
            color: (subcategoryInfo?.input_type === 'file' && !state.file) ? 'var(--text-muted, #6b7280)' : '#fff',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: (subcategoryInfo?.input_type === 'file' && !state.file) ? 'not-allowed' : 'pointer',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  )
}
